import axios from 'axios';

import type {
  ActivePreset,
  BeVirtualPreset,
  GetPresetResponse,
  GetPresetsResponse,
  PostPresetRequest,
  PostPresetResponse,
  Preset,
  PutPresetRenameRequest,
  PutPresetRenameResponse,
  PutPresetRequest,
  PutPresetResponse,
  VirtualPreset,
} from '../types/api/preset';
import {AXIOS_BASE_CONFIG, makeInstanceConfig} from './axiosConfig';
import type {ApiContext} from './types';

export function createPresetApi(ctx: ApiContext) {
  const BASE = `/v6/data-grid/${ctx.gridCode}`;
  const instanceConfig = makeInstanceConfig(ctx.presetInstanceId);

  const getPreset = (presetId: string) =>
    axios
      .get<GetPresetResponse>(`${BASE}/preset/${presetId}`, instanceConfig)
      .then((response) => response.data)
      .catch(ctx.handleApiError);

  const getVirtualPreset = () =>
    axios
      .get<BeVirtualPreset>(`${BASE}/virtual-preset`, instanceConfig)
      .then((response) => response.data)
      .catch(ctx.handleApiError);

  const getActivePreset = (
    virtualPreset: VirtualPreset,
    customPresets: Preset[]
  ): Promise<ActivePreset | void> => {
    const activeCustomPreset = customPresets.find((customPreset) => customPreset.isActive);

    if (!activeCustomPreset) {
      return Promise.resolve(virtualPreset);
    }

    return getPreset(activeCustomPreset.id);
  };

  return {
    getActivePreset,
    getPreset,
    getVirtualPreset,
    deleteVirtualPreset: () =>
      axios
        .delete(`${BASE}/virtual-preset`, AXIOS_BASE_CONFIG)
        .then(() => getVirtualPreset())
        .catch(ctx.handleApiError),
    createPreset: (presetRequest: PostPresetRequest) =>
      axios
        .post<PostPresetResponse>(
          `${BASE}/preset`,
          {
            ...presetRequest,
            instance: ctx.presetInstanceId ?? null,
          },
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    updatePreset: (presetId: string, presetRequest: PutPresetRequest) =>
      axios
        .put<PutPresetResponse>(
          `${BASE}/preset/${presetId}`,
          {
            ...presetRequest,
            instance: ctx.presetInstanceId ?? null,
          },
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    updateRenamePreset: (presetId: string, presetRequest: PutPresetRenameRequest) =>
      axios
        .put<PutPresetRenameResponse>(
          `${BASE}/preset/${presetId}/rename`,
          {
            ...presetRequest,
            instance: ctx.presetInstanceId ?? null,
          },
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    deletePreset: (presetId: string) =>
      axios.delete(`${BASE}/preset/${presetId}`, AXIOS_BASE_CONFIG).catch(ctx.handleApiError),
    presetOrder: (presetsOrder: string[]) =>
      axios.put(`${BASE}/preset/order`, {presetsOrder}, instanceConfig).catch(ctx.handleApiError),
    activatePreset: (activePresetId: string | null) =>
      axios
        .put(`${BASE}/preset/active`, {activePreset: activePresetId}, instanceConfig)
        .catch(ctx.handleApiError),
    getPresets: () =>
      axios
        .get<GetPresetsResponse>(`${BASE}/preset`, instanceConfig)
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    updateVirtualPreset: (virtualPreset: VirtualPreset) =>
      axios.put(`${BASE}/virtual-preset`, virtualPreset, instanceConfig).catch(ctx.handleApiError),
  };
}
